/**
 * X-Slider Web Component
 *
 * A high-performance carousel slider supporting both transform-based sliding
 * and opacity-based fading, with interactive drag, responsive column layouts,
 * and viewport-aware autoplay.
 *
 * @example
 * ```html
 * <x-slider
 *   columns="4"
 *   tablet="2"
 *   mobile="1"
 *   gap="20"
 *   arrows
 *   loop
 *   pagination="bullets"
 *   adaptive-height
 *   aria-label="Featured products"
 * >
 *   <div>Slide 1</div>
 *   <div>Slide 2</div>
 * </x-slider>
 * ```
 *
 * @attribute {string} transition - Transition mode: 'slide' (default) or 'fade'.
 *   Fade mode stacks slides and uses opacity transitions.
 */

if (!customElements.get('x-slider')) {
  /** Main slide transition (GPU-optimized) */
  const TRANSITION = 'transform 400ms cubic-bezier(0.25, 1, 0.5, 1)';

  /** Fade transition for opacity mode */
  const TRANSITION_FADE = 'opacity 600ms cubic-bezier(0.4, 0, 0.2, 1)';

  /** Height transition for adaptive-height mode */
  const TRANSITION_HEIGHT = 'height 300ms cubic-bezier(0.4, 0, 0.2, 1)';

  /** Pixels to move before locking swipe direction */
  const DIRECTION_THRESHOLD = 10;

  /** Distance (as % of width) required to trigger a slide change */
  const SWIPE_THRESHOLD_PERCENT = 0.15;

  /** Minimum pixel distance for a swipe, regardless of container width */
  const SWIPE_THRESHOLD_MIN_PX = 40;

  /** Pixels per ms required to trigger a "flick" swipe */
  const VELOCITY_THRESHOLD = 0.3;

  /** Maximum duration in ms for a gesture to be considered a flick */
  const MAX_SWIPE_TIME = 300;

  /** Inline SVG for chevron-left arrow */
  const CHEVRON_LEFT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"></polyline></svg>`;

  /** Inline SVG for chevron-right arrow */
  const CHEVRON_RIGHT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>`;

  /** Tailwind classes for navigation arrow buttons */
  const NAV_BUTTON_CLASSES = [
    'tw-absolute tw-top-1/2 -tw-translate-y-1/2 tw-z-10',
    'tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full',
    'tw-bg-white/80 tw-border tw-border-gray-200 tw-shadow-sm',
    'tw-text-gray-800 tw-transition-all tw-duration-200',
    'hover:tw-bg-white hover:tw-shadow-md active:tw-scale-95',
    'focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-500',
    'disabled:tw-opacity-0 disabled:tw-pointer-events-none',
  ].join(' ');

  customElements.define(
    'x-slider',
    class XSlider extends HTMLElement {
      static get observedAttributes() {
        return [
          'columns',
          'tablet',
          'mobile',
          'gap',
          'loop',
          'autoplay',
          'arrows',
          'pagination',
          'adaptive-height',
          'transition',
        ];
      }

      constructor() {
        super();
        this.track = null;
        this.items = [];
        this.paginationContainer = null;
        this.index = 0;
        this.autoPlayTimer = null;
        this.autoplayPaused = false;
        this.isIntersecting = false;
        this.didSwipe = false;
        this._initialized = false;
        this.sliderId = `slider-${Math.random().toString(36).slice(2, 9)}`;

        // Observers
        this.resizeObserver = null;
        this.mutationObserver = null;
        this.intersectionObserver = null;

        // Pointer state
        this.pointer = {
          id: null,
          startX: 0,
          startY: 0,
          startTime: 0,
          lastX: 0,
          lastTime: 0,
          startOffset: 0,
          direction: null,
        };

        // Reduced motion
        this.reducedMotion = false;
        this.motionQuery = null;
      }

      /** Check if this slider uses fade transition */
      get isFade() {
        return this.getAttribute('transition') === 'fade';
      }

      connectedCallback() {
        // Defer to ensure children are parsed
        if (this.children.length === 0) {
          const observer = new MutationObserver(() => {
            if (this.children.length > 0) {
              observer.disconnect();
              this._setup();
            }
          });
          observer.observe(this, { childList: true });
        } else {
          this._setup();
        }
      }

      _setup() {
        if (this._initialized) return;

        // Render phase
        this._setupAccessibility();
        this._setupStructure();
        this._setupNavigation();
        this._setupPagination();

        // Init phase
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion = this.motionQuery.matches;
        this.motionQuery.addEventListener('change', (e) => {
          this.reducedMotion = e.matches;
          this.refresh();
        });

        this._setupObservers();
        if (!this.isFade) {
          this._setupPointerEvents();
        }
        this._setupKeyboard();
        this._setupAutoplayPause();

        this.refresh();

        this.classList.remove('pending');
        this.querySelectorAll('.pending').forEach((el) => el.classList.remove('pending'));

        this._initialized = true;
      }

      attributeChangedCallback(name, oldVal, newVal) {
        if (!this._initialized || oldVal === newVal) return;
        this.refresh();

        if (name === 'autoplay') {
          this._getNum('autoplay') > 0 ? this._startAutoPlay() : this._stopAutoPlay();
        }
      }

      disconnectedCallback() {
        this._stopAutoPlay();
        this.resizeObserver?.disconnect();
        this.mutationObserver?.disconnect();
        this.intersectionObserver?.disconnect();
      }

      // ─────────────────────────────────────────────────────────────
      // Attribute helpers
      // ─────────────────────────────────────────────────────────────

      _getNum(attr, fallback = 0) {
        const val = parseFloat(this.getAttribute(attr));
        return isNaN(val) ? fallback : val;
      }

      _getBool(attr) {
        return this.hasAttribute(attr);
      }

      // ─────────────────────────────────────────────────────────────
      // Internal Setup
      // ─────────────────────────────────────────────────────────────

      _setupAccessibility() {
        this.setAttribute('role', 'region');
        this.setAttribute('aria-roledescription', 'carousel');
        if (!this.hasAttribute('aria-label')) {
          this.setAttribute('aria-label', 'Content slider');
        }
        if (!this.hasAttribute('tabindex')) {
          this.setAttribute('tabindex', '0');
        }

        if (!this.querySelector('[data-live-region]')) {
          const liveRegion = document.createElement('div');
          liveRegion.setAttribute('aria-live', 'polite');
          liveRegion.setAttribute('aria-atomic', 'true');
          liveRegion.className = 'tw-sr-only';
          liveRegion.id = `${this.sliderId}-live`;
          liveRegion.setAttribute('data-live-region', '');
          this.appendChild(liveRegion);
        }
      }

      _setupStructure() {
        let track = this.querySelector('[data-track]');

        if (!track) {
          const children = Array.from(this.children).filter(
            (child) =>
              child.tagName !== 'BUTTON' &&
              !child.hasAttribute('data-slider-nav') &&
              !child.hasAttribute('data-pagination') &&
              !child.hasAttribute('data-live-region'),
          );

          track = document.createElement('ul');
          track.setAttribute('data-track', '');

          children.forEach((child) => {
            const li = document.createElement('li');
            li.appendChild(child);
            track.appendChild(li);
          });

          this.appendChild(track);
        }

        track.style.margin = '0';
        track.style.padding = '0';
        track.style.listStyle = 'none';
        track.setAttribute('role', 'group');
        track.setAttribute('aria-label', 'Slides');
        track.id = `${this.sliderId}-track`;

        if (this.isFade) {
          // Fade mode: stack slides on top of each other
          track.className = 'tw-relative tw-w-full';
          track.style.transition = 'none';
        } else {
          // Slide mode: horizontal flex layout
          track.className = 'tw-flex tw-will-change-transform';
          track.style.transition = this.reducedMotion ? 'none' : TRANSITION;
        }

        this.track = track;
        this.items = Array.from(this.track.children);

        this.items.forEach((item, i) => {
          item.setAttribute('role', 'group');
          item.setAttribute('aria-roledescription', 'slide');
          item.setAttribute('aria-label', `Slide ${i + 1} of ${this.items.length}`);

          if (this.isFade) {
            // Fade: stack absolutely, control visibility with opacity
            item.style.position = i === 0 ? 'relative' : 'absolute';
            item.style.top = '0';
            item.style.left = '0';
            item.style.width = '100%';
            item.style.opacity = i === 0 ? '1' : '0';
            item.style.transition = this.reducedMotion ? 'none' : TRANSITION_FADE;
            item.style.pointerEvents = i === 0 ? '' : 'none';
          } else {
            // Slide: flex items
            item.className = 'tw-flex-shrink-0';
          }
        });

        this.className = 'tw-block tw-relative tw-overflow-hidden focus:tw-outline-none';

        if (this._getBool('adaptive-height')) {
          this.style.transition = TRANSITION_HEIGHT;
        }
      }

      _setupNavigation() {
        if (this._getBool('arrows') && !this.querySelector('[data-prev]')) {
          this.insertAdjacentHTML(
            'beforeend',
            `<div data-slider-nav class="tw-pointer-events-none tw-absolute tw-inset-0 tw-z-10">
              <button type="button" data-prev aria-label="Go to previous slide" aria-controls="${this.sliderId}-track" class="${NAV_BUTTON_CLASSES} tw-left-4 tw-pointer-events-auto">${CHEVRON_LEFT_SVG}</button>
              <button type="button" data-next aria-label="Go to next slide" aria-controls="${this.sliderId}-track" class="${NAV_BUTTON_CLASSES} tw-right-4 tw-pointer-events-auto">${CHEVRON_RIGHT_SVG}</button>
            </div>`,
          );

          this.querySelector('[data-prev]')?.addEventListener('click', () => this.prev());
          this.querySelector('[data-next]')?.addEventListener('click', () => this.next());
        }
      }

      _setupPagination() {
        const type = this._getPaginationType();
        if (type === 'none' || this.querySelector('[data-pagination]')) return;

        const container = document.createElement('div');
        container.setAttribute('data-pagination', type);
        container.className = 'tw-flex tw-justify-center tw-gap-2 tw-mt-6';

        if (type === 'bullets') {
          container.setAttribute('role', 'tablist');
          container.setAttribute('aria-label', 'Slide navigation');
        } else if (type === 'fraction') {
          container.className =
            'tw-flex tw-justify-center tw-gap-1 tw-mt-6 tw-text-sm tw-font-medium tw-text-gray-500';
        } else if (type === 'progressbar') {
          container.className =
            'tw-mt-6 tw-h-1 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden tw-relative';
          container.innerHTML =
            '<div data-fill class="tw-absolute tw-inset-y-0 tw-left-0 tw-bg-blue-600 tw-rounded-full tw-transition-transform tw-duration-300 tw-origin-left tw-w-full tw-scale-x-0"></div>';
        }

        this.appendChild(container);
        this.paginationContainer = container;
      }

      _setupObservers() {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this);

        this.mutationObserver = new MutationObserver((mutations) => {
          const hasStructureChange = mutations.some(
            (m) => m.type === 'childList' && m.target === this.track,
          );
          if (hasStructureChange) {
            this.items = Array.from(this.track.children);
            this.refresh();
          }
        });
        if (this.track) {
          this.mutationObserver.observe(this.track, { childList: true });
        }

        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            this.isIntersecting = entry.isIntersecting;
            this.isIntersecting ? this._resumeAutoPlay() : this._pauseAutoPlay();
          },
          { threshold: 0.1 },
        );
        this.intersectionObserver.observe(this);
      }

      // ─────────────────────────────────────────────────────────────
      // Interaction
      // ─────────────────────────────────────────────────────────────

      _setupPointerEvents() {
        this.addEventListener('dragstart', (e) => e.preventDefault());

        this.addEventListener('pointerdown', (e) => {
          const target = e.target;
          if (target.closest('[data-slider-nav], [data-pagination]')) return;
          if (this.pointer.id !== null) return;

          const now = performance.now();
          this.pointer.id = e.pointerId;
          this.pointer.startX = e.clientX;
          this.pointer.startY = e.clientY;
          this.pointer.startTime = now;
          this.pointer.lastX = e.clientX;
          this.pointer.lastTime = now;
          this.pointer.startOffset = this._getCurrentOffset();
          this.pointer.direction = null;
          this.didSwipe = false;

          if (e.pointerType === 'mouse') {
            e.preventDefault();
            this.setPointerCapture(e.pointerId);
          }

          if (this.track) {
            this.track.style.transition = 'none';
          }
        });

        this.addEventListener('pointermove', (e) => {
          if (e.pointerId !== this.pointer.id) return;

          const dx = e.clientX - this.pointer.startX;
          const dy = e.clientY - this.pointer.startY;

          if (
            !this.pointer.direction &&
            (Math.abs(dx) > DIRECTION_THRESHOLD || Math.abs(dy) > DIRECTION_THRESHOLD)
          ) {
            this.pointer.direction = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
          }

          if (this.pointer.direction === 'h') {
            const newOffset = this.pointer.startOffset - dx;
            this._updatePosition(newOffset);
            this.pointer.lastX = e.clientX;
            this.pointer.lastTime = performance.now();
          }
        });

        this.addEventListener('pointerup', (e) => {
          if (e.pointerId !== this.pointer.id) return;

          if (this.pointer.direction === 'h') {
            const dx = e.clientX - this.pointer.startX;
            const dt = performance.now() - this.pointer.startTime;
            const velocity = Math.abs(dx) / dt;

            const containerWidth = this.getBoundingClientRect().width;
            const threshold = Math.max(
              containerWidth * SWIPE_THRESHOLD_PERCENT,
              SWIPE_THRESHOLD_MIN_PX,
            );

            if (
              Math.abs(dx) > threshold ||
              (dt < MAX_SWIPE_TIME && velocity > VELOCITY_THRESHOLD)
            ) {
              this.didSwipe = true;
              dx > 0 ? this.prev() : this.next();
            } else {
              this.goTo(this.index);
            }
          }

          this.pointer.id = null;
          if (this.track) {
            this.track.style.transition = this.reducedMotion ? 'none' : TRANSITION;
          }
        });

        this.addEventListener('pointercancel', () => {
          this.pointer.id = null;
          this.goTo(this.index);
        });

        this.addEventListener(
          'click',
          (e) => {
            if (this.didSwipe) {
              e.preventDefault();
              e.stopPropagation();
              this.didSwipe = false;
            }
          },
          { capture: true },
        );
      }

      _setupKeyboard() {
        this.addEventListener('keydown', (e) => {
          switch (e.key) {
            case 'ArrowLeft':
              e.preventDefault();
              this.prev();
              break;
            case 'ArrowRight':
              e.preventDefault();
              this.next();
              break;
            case 'Home':
              e.preventDefault();
              this.goTo(0);
              break;
            case 'End':
              e.preventDefault();
              this.goTo(this._getMaxIndex());
              break;
          }
        });
      }

      _setupAutoplayPause() {
        this.addEventListener('mouseenter', () => this._pauseAutoPlay());
        this.addEventListener('mouseleave', () => this._resumeAutoPlay());
        this.addEventListener('focusin', () => this._pauseAutoPlay());
        this.addEventListener('focusout', () => this._resumeAutoPlay());
        this.addEventListener('pointerdown', () => this._pauseAutoPlay());
      }

      // ─────────────────────────────────────────────────────────────
      // Public API
      // ─────────────────────────────────────────────────────────────

      refresh() {
        if (this.isFade) {
          this._updateFade();
        } else {
          const gap = this._getNum('gap', 16);
          const cols = this._getResponsiveColumns();
          const containerWidth = this.getBoundingClientRect().width;

          const totalGap = (cols - 1) * gap;
          const slideWidth = (containerWidth - totalGap) / cols;

          if (this.track) {
            this.track.style.gap = `${gap}px`;
          }

          this.items.forEach((item) => {
            item.style.width = `${slideWidth}px`;
          });
        }

        this.index = Math.max(0, Math.min(this.index, this._getMaxIndex()));

        if (!this.isFade) {
          this._updatePosition();
        }
        this._updateArrows();
        this._updatePagination();
        this._updateInert();
        this._updateAdaptiveHeight();

        if (this._getNum('autoplay') > 0 && this.isIntersecting) {
          this._startAutoPlay();
        }
      }

      goTo(index) {
        const maxIndex = this._getMaxIndex();

        if (this._getBool('loop')) {
          if (index < 0) index = maxIndex;
          else if (index > maxIndex) index = 0;
        } else {
          index = Math.max(0, Math.min(index, maxIndex));
        }

        this.index = index;

        if (this.isFade) {
          this._updateFade();
        } else {
          this._updatePosition();
        }

        this._updateArrows();
        this._updatePagination();
        this._updateInert();
        this._updateAdaptiveHeight();
        this._announceSlide();

        this.dispatchEvent(new CustomEvent('slide-change', { detail: { index }, bubbles: true }));
      }

      next() {
        this.goTo(this.index + 1);
      }

      prev() {
        this.goTo(this.index - 1);
      }

      toggleAutoplay() {
        if (this.autoPlayTimer) {
          this._stopAutoPlay();
          this.autoplayPaused = true;
          return false;
        } else {
          this._startAutoPlay();
          this.autoplayPaused = false;
          return true;
        }
      }

      // ─────────────────────────────────────────────────────────────
      // Private Helpers
      // ─────────────────────────────────────────────────────────────

      /** Fade mode: update opacity on all items */
      _updateFade() {
        this.items.forEach((item, i) => {
          const active = i === this.index;
          item.style.opacity = active ? '1' : '0';
          item.style.pointerEvents = active ? '' : 'none';
          // First item stays relative to hold height; others absolute
          item.style.position = active ? 'relative' : 'absolute';
          item.style.zIndex = active ? '1' : '0';
        });
      }

      /** Slide mode: update translateX with hardware acceleration */
      _updatePosition(customOffset) {
        if (!this.track || this.items.length === 0) return;

        const offset = customOffset ?? this._getOffsetForIndex(this.index);
        this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;
      }

      _getCurrentOffset() {
        if (!this.track) return 0;
        const style = window.getComputedStyle(this.track);
        const matrix = new DOMMatrixReadOnly(style.transform);
        return -matrix.m41;
      }

      _getOffsetForIndex(index) {
        if (this.items.length === 0) return 0;
        const gap = this._getNum('gap', 16);
        const slideWidth = this.items[0].getBoundingClientRect().width;
        return index * (slideWidth + gap);
      }

      _updateAdaptiveHeight() {
        if (!this._getBool('adaptive-height') || this.items.length === 0) return;

        const cols = this._getResponsiveColumns();
        const visibleItems = this.items.slice(this.index, this.index + cols);

        let maxHeight = 0;
        visibleItems.forEach((item) => {
          const height = item.firstElementChild?.scrollHeight || item.scrollHeight;
          if (height > maxHeight) maxHeight = height;
        });

        if (maxHeight > 0) {
          this.style.height = `${maxHeight}px`;
        }
      }

      _updateInert() {
        const cols = this.isFade ? 1 : this._getResponsiveColumns();
        this.items.forEach((item, i) => {
          const visible = i >= this.index && i < this.index + cols;
          item.toggleAttribute('inert', !visible);
          item.setAttribute('aria-hidden', String(!visible));
        });
      }

      _updateArrows() {
        const prevBtn = this.querySelector('[data-prev]');
        const nextBtn = this.querySelector('[data-next]');
        if (!prevBtn || !nextBtn) return;

        if (this._getBool('loop')) {
          prevBtn.disabled = false;
          nextBtn.disabled = false;
        } else {
          prevBtn.disabled = this.index === 0;
          nextBtn.disabled = this.index >= this._getMaxIndex();
        }
      }

      _updatePagination() {
        const type = this._getPaginationType();
        const container = this.paginationContainer || this.querySelector('[data-pagination]');
        if (!container) return;

        const maxIndex = this._getMaxIndex();

        if (type === 'bullets') {
          this._renderBullets(container, maxIndex);
        } else if (type === 'fraction') {
          container.innerHTML = `<span class="tw-text-gray-800">${this.index + 1}</span> / <span>${maxIndex + 1}</span>`;
        } else if (type === 'progressbar') {
          const fill = container.querySelector('[data-fill]');
          if (fill) {
            const progress = (this.index + 1) / (maxIndex + 1);
            fill.style.transform = `scaleX(${progress})`;
          }
        }
      }

      _renderBullets(container, maxIndex) {
        const count = maxIndex + 1;
        const currentButtons = container.querySelectorAll('button');

        if (currentButtons.length !== count) {
          container.innerHTML = Array.from({ length: count }, (_, i) => {
            return `<button type="button" role="tab" data-index="${i}" aria-label="Go to slide ${i + 1}" class="tw-w-2 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300 tw-bg-gray-800/20 hover:tw-bg-gray-800/40 aria-selected:tw-bg-gray-800 aria-selected:tw-w-6"></button>`;
          }).join('');

          container.querySelectorAll('button').forEach((btn) => {
            btn.addEventListener('click', () => this.goTo(Number(btn.dataset.index)));
          });
        }

        container.querySelectorAll('button').forEach((btn, i) => {
          const active = i === this.index;
          btn.setAttribute('aria-selected', String(active));
          btn.setAttribute('tabindex', active ? '0' : '-1');
        });
      }

      _announceSlide() {
        const liveRegion = this.querySelector('[data-live-region]');
        if (liveRegion) {
          liveRegion.textContent = `Showing slide ${this.index + 1} of ${this.items.length}`;
        }
      }

      _getPaginationType() {
        const attr = this.getAttribute('pagination');
        if (attr) return attr;
        if (this.hasAttribute('bullets')) return 'bullets';
        return 'none';
      }

      _getResponsiveColumns() {
        const width = window.innerWidth;
        if (width < 640) return this._getNum('mobile', 1);
        if (width < 1024) return this._getNum('tablet', 2);
        return this._getNum('columns', 3);
      }

      _getMaxIndex() {
        if (this.isFade) return Math.max(0, this.items.length - 1);
        return Math.max(0, this.items.length - this._getResponsiveColumns());
      }

      _startAutoPlay() {
        const ms = this._getNum('autoplay', 0);
        if (ms <= 0 || this.reducedMotion || !this.isIntersecting) return;

        this._stopAutoPlay();
        this.autoPlayTimer = window.setInterval(() => this.next(), ms);
      }

      _stopAutoPlay() {
        if (this.autoPlayTimer) {
          clearInterval(this.autoPlayTimer);
          this.autoPlayTimer = null;
        }
      }

      _pauseAutoPlay() {
        this._stopAutoPlay();
      }

      _resumeAutoPlay() {
        if (!this.autoplayPaused && this._getNum('autoplay') > 0) {
          this._startAutoPlay();
        }
      }
    },
  );
}
